import { getFilesFromDirectory, saveFilesIntoADirectory } from './files';
import { planUploads, type PlannedUpload } from './imagePlan';
import type { OrderMetadata } from './imageNaming';
import {
  addImageUploadBatch,
  OFFLINE_PENDING_UPLOADS_DIR,
} from '../offline/offlineImageUploadQueue';
import type { OrderType } from '../types/orders';
import type { EntityType } from './storage';
import type { FormImageSource } from '../offline/formStorage';
import { photoFingerprint } from '../offline/photoFingerprint';
import type { PhotoHandoff } from '../offline/photoHandoff';

export interface EnqueuePhotosArgs {
  /** Directorio OPFS del formulario ('action' / 'accident'). */
  picturesDirectoryName: string;
  orderId: string;
  orderType: OrderType;
  entityType: EntityType;
  orderMetadata: OrderMetadata;
  /**
   * Nombres de fichero a encolar. **Obligatorio en los flujos de EDICIÓN**: el
   * formulario carga en OPFS también las fotos que ya están en la base de
   * datos, así que sin filtro se re-suben y se duplican.
   */
  onlyNames?: string[];
  /** Ver `PlanUploadsOptions.fromSequence`. Por defecto 0. */
  fromSequence?: number;
  /**
   * Plan ya calculado con `planUploads`. La creación de la actuación lo
   * necesita ANTES de encolar (el borrado de imágenes compara contra él) y
   * encola al final; pasarlo evita planificar dos veces sobre el mismo OPFS.
   */
  plan?: PlannedUpload[];
  /** De dónde leer las fotos (por defecto, la carpeta OPFS de la pantalla). */
  images?: Pick<FormImageSource, 'list'>;
  /**
   * Solo en los reintentos de la cola compuesta (`FormContext.photoHandoff`):
   * si la operación ya entregó sus fotos, se devuelve el plan sin volver a
   * encolarlas (spec 2026-10-01-photo-outbox-identity § 10.4 E2).
   */
  handoff?: PhotoHandoff;
}

/**
 * Nombre del fichero pendiente de una foto en la bandeja.
 *
 * Lleva un componente ÚNICO por encolado (spec 2026-10-01-photo-outbox-identity
 * § 5.1). Era `pending_photo_<orden>_<secuencia>_<origen>`, determinista, y como
 * `saveFilesIntoADirectory` no pisa un fichero que ya existe:
 *  - re-encolar la misma foto apuntaba al fichero de la entrada anterior, y el
 *    dedupe de entonces lo borraba (30-sep: 16 de 19 fotos perdidas);
 *  - dos fotos distintas con el mismo nombre de origen y número se fundían;
 *  - un huérfano antiguo con ese nombre se subía en lugar de la foto nueva.
 * Se conservan orden y secuencia en el nombre para poder depurar.
 */
export const makePendingPhotoName = (
  orderId: string,
  sequence: number,
  sourceName: string,
): string => {
  const unique = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
  return `pending_photo_${orderId}_${sequence}_${unique}_${sourceName}`;
};

/**
 * Encola las fotos del asistente para subirlas en segundo plano y devuelve el
 * PLAN de lo que el usuario eligió.
 *
 * ─── Por qué devuelve el plan y no las subidas ─────────────────────────────
 *
 * `NewAction` calcula qué imágenes borrar de la BD comparando contra la lista
 * de subidas. Con el outbox esa lista está vacía al guardar, así que **hay que
 * comparar contra el plan** — la intención del usuario. Devolverlo no es un
 * detalle: es lo que evita que un re-guardado borre las fotos previas.
 *
 * ─── Por qué se copian los blobs a otro directorio ─────────────────────────
 *
 * `removeNewActionStorage()` / `removeNewAccidentStorage()` borran el
 * directorio de trabajo al terminar cada saveData, incluso cuando el guardado
 * fue bien. Si los blobs pendientes se quedaran ahí, el cleanup los destruiría
 * antes de que la cola llegue a subirlos. Por eso existe
 * `OFFLINE_PENDING_UPLOADS_DIR`.
 */
export async function enqueuePhotosForUpload({
  picturesDirectoryName,
  orderId,
  orderType,
  entityType,
  orderMetadata,
  onlyNames,
  fromSequence,
  plan: givenPlan,
  images,
  handoff,
}: EnqueuePhotosArgs): Promise<PlannedUpload[]> {
  const plan =
    givenPlan ??
    (await planUploads(picturesDirectoryName, orderMetadata, {
      onlyNames,
      fromSequence,
      images,
    }));
  if (plan.length === 0) return [];

  // Un intento anterior de esta operación ya las entregó: pueden estar
  // esperando en la bandeja o subidas ya, y en ese caso la huella no tendría con
  // qué compararlas. El plan se devuelve igual: el alta compara los borrados
  // contra él.
  if (handoff?.done) {
    console.log(
      `[enqueuePhotos] la operación ya entregó sus fotos; no se vuelven a encolar (orderId=${orderId}, ${plan.length} en el plan)`,
    );
    return plan;
  }

  const entries = images
    ? await images.list(picturesDirectoryName)
    : await getFilesFromDirectory(picturesDirectoryName);
  const sourceByName = new Map(entries.map((e) => [e.file.name, e.file]));

  // Una copia por foto con nombre ÚNICO y su huella de contenido. En serie y no
  // con `Promise.all`: con 20 fotos serían 20 copias en memoria a la vez en una
  // tablet.
  const pending: Array<{ file: File; sequence: number; contentHash: string | null }> = [];
  for (const p of plan) {
    const source = sourceByName.get(p.blobFileName);
    if (!source) continue;
    pending.push({
      file: new File([source], makePendingPhotoName(orderId, p.sequence, p.blobFileName), {
        type: source.type,
      }),
      sequence: p.sequence,
      contentHash: await photoFingerprint(source),
    });
  }

  if (pending.length === 0) return [];

  await saveFilesIntoADirectory(
    OFFLINE_PENDING_UPLOADS_DIR,
    pending.map((item) => item.file),
  );

  // Si una foto ya estaba en la bandeja, la cola conserva la que hay y borra
  // la copia recién escrita (spec 2026-10-01-photo-outbox-identity § 5.3).
  await addImageUploadBatch(
    pending.map((item) => ({
      kind: 'photo' as const,
      orderId,
      orderType,
      blobDirectory: OFFLINE_PENDING_UPLOADS_DIR,
      blobFileName: item.file.name,
      orderMetadata,
      photoSequence: item.sequence,
      ...(item.contentHash && { contentHash: item.contentHash }),
    })),
  );
  // Solo tras encolar: si algo falla antes, el siguiente intento lo repite y la
  // huella evita duplicar las que sigan en la bandeja.
  await handoff?.markDone();

  return plan;
}

/**
 * `entityType` que espera el token prefirmado a partir del tipo de orden.
 * Existe para que los cuatro `saveData` no lo escriban a mano cada uno.
 */
export function entityTypeForOrderType(orderType: OrderType): EntityType {
  switch (String(orderType)) {
    case 'accident':
      return 'accident';
    // El tramo del recorrido tiene prefijo propio (ver `EntityType`).
    case 'routeSegment':
      return 'segment';
    default:
      return 'action';
  }
}
